import React, { useEffect, useState, useMemo, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import UserDropdown from "@shared/components/UserDropdown";
import ChangePasswordModal from "@shared/components/ChangePasswordModal";
import MyProfileModal from "@shared/components/MyProfileModal";
import EnvironmentPill from "@shared/components/EnvironmentPill";
import Footer from "@shared/components/Footer";
import fetchAvailableApps from "@shared/api/fetchAvailableApps";
import { post, apiUrl, getAuthHeaders } from "@shared/utils/api";
import { setAuthToken } from "@shared/utils/tokenManager";
import { isUserManagementConfigured } from "@shared/api/userManagement";
import { sanitizeUrlStrippingAuthCallbackParams } from "@shared/utils/authCallbackUrl";
import { getAdminViewFromPath, isAdminPath, parseAdminPath, isAppScopedView, buildAdminPath } from "../config/routes";
import AdminUsersPage from "./admin/AdminUsersPage";
import AdminRolesPage from "./admin/AdminRolesPage";
import AdminPermissionGroupsPage from "./admin/AdminPermissionGroupsPage";

const LAST_LAUNCH_URL_KEY = "odp_last_launch_url";

export default function Dashboard({
  brand,
  customerKey,
  onLogout,
  usernameOrEmail
}) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [errorModal, setErrorModal] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pathname, setPathname] = useState(() => typeof window !== "undefined" ? window.location.pathname : "");
  const [userManagementConfigured, setUserManagementConfigured] = useState(null);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [myProfileModalOpen, setMyProfileModalOpen] = useState(false);

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback((path) => {
    window.history.pushState(null, "", path);
    setPathname(path);
  }, []);

  const hasAccess = useMemo(() => usernameOrEmail?.can_manage_customer_users === true, [usernameOrEmail]);
  const adminView = getAdminViewFromPath(pathname);
  const pageTitle = !adminView
    ? "Apps"
    : { users: "Users", roles: "Roles", "permission-groups": "Permission Groups" }[adminView] || "Apps";

  useEffect(() => {
    if (hasAccess && customerKey) {
      isUserManagementConfigured(customerKey).then((configured) => {
        setUserManagementConfigured(configured);
        if (typeof window !== "undefined" && (process.env.NODE_ENV === "development" || new URLSearchParams(window.location.search).get("um_debug") === "1")) {
          console.log("[User Management debug] isUserManagementConfigured(", customerKey, ") =>", configured);
        }
      });
    } else {
      setUserManagementConfigured(false);
    }
  }, [hasAccess, customerKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (process.env.NODE_ENV !== "development" && new URLSearchParams(window.location.search).get("um_debug") !== "1") {
      return;
    }
    console.log("[User Management debug]", {
      hasAccess,
      userManagementConfigured,
      customerKey,
      role: usernameOrEmail?.role,
      can_manage_customer_users: usernameOrEmail?.can_manage_customer_users,
      reason_no_tab: !hasAccess ? "user role not in allowed list (UserAdministrator, HqAdmin or ODP_CUSTOMER_USER_ADMINISTRATION_ROLE)" : !userManagementConfigured ? "customer has no vmm_db_url in vmm_provisionings" : null,
    });
  }, [hasAccess, userManagementConfigured, customerKey, usernameOrEmail?.role, usernameOrEmail?.can_manage_customer_users]);

  const { appSlug: pathAppSlug } = parseAdminPath(pathname);
  const selectedAppFromPath = useMemo(
    () => (pathAppSlug && apps.length ? apps.find((a) => (a.slug || a.appSlug) === pathAppSlug) ?? null : null),
    [pathAppSlug, apps]
  );
  const appSlugForNav = pathAppSlug ?? (apps[0] ? (apps[0].slug ?? apps[0].appSlug ?? apps[0].id) : null);

  useEffect(() => {
    if (isAdminPath(pathname) && (!hasAccess || !userManagementConfigured)) {
      const base = pathname.split("/customer/admin/")[0] || "/";
      navigate(base || "/");
    }
  }, [pathname, hasAccess, userManagementConfigured, navigate]);

  useEffect(() => {
    if (!hasAccess || !userManagementConfigured || loading || !customerKey) return;
    const view = getAdminViewFromPath(pathname);
    if (!isAppScopedView(view)) return;
    if (pathAppSlug && apps.length > 0 && !selectedAppFromPath) {
      const firstSlug = (apps[0].slug ?? apps[0].appSlug ?? apps[0].id);
      const path = buildAdminPath(pathname, view, firstSlug);
      navigate(path);
    }
  }, [pathname, pathAppSlug, selectedAppFromPath, apps, loading, hasAccess, userManagementConfigured, customerKey, navigate]);

  const userProfile = useMemo(() => {
    const role = usernameOrEmail?.role || "";
    const rolesFromApi = usernameOrEmail?.rolesDisplay ?? usernameOrEmail?.roles_display;
    const rolesDisplay = Array.isArray(rolesFromApi) && rolesFromApi.length > 0
      ? rolesFromApi
      : (role ? [role] : []);
    return {
      name: usernameOrEmail?.name || usernameOrEmail?.email || "",
      firstName: usernameOrEmail?.firstName || "",
      lastName: usernameOrEmail?.lastName || "",
      email: usernameOrEmail?.email || usernameOrEmail?.name || usernameOrEmail || "",
      role,
      rolesDisplay,
      apps: usernameOrEmail?.apps || [],
      appsDisplay: usernameOrEmail?.appsDisplay || usernameOrEmail?.apps_display || [],
      profileImage: usernameOrEmail?.avatar || ""
    };
  }, [usernameOrEmail]);

  const _silentRefresh = async () => {
    const response = await post('/api/v1/auth/refresh', {});

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    if (data.token) {
      setAuthToken(data.token);
      return data.token;
    }
    throw new Error('No token in refresh response');
  };

  useEffect(() => {
    const loadApps = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!customerKey) {
          setError('Customer information not available');
          setLoading(false);
          return;
        }

        // Fetch available apps with launch URLs for this customer (inferred from subdomain)
        const appsWithUrls = await fetchAvailableApps(customerKey);

        const transformedApps = appsWithUrls.map(app => ({
          ...app,
          launchUrl: app.launchUrl || '',
          logoUrl: app.logo_url || app.logoUrl || '',
          displayName: app.display_name || app.displayName || app.aliasName || app.alias_name || app.name || ''
        }));

        setApps(transformedApps);
      } catch {
        setError('Failed to load applications');
      } finally {
        setLoading(false);
      }
    };

    loadApps();
  }, [customerKey]);

  const handleAppClick = async (appId) => {
    try {
      const app = apps.find(a => a.id === appId || a.appId === appId);
      if (!app) {
        alert('App not found');
        return;
      }
      if (!app.launchUrl) {
        alert(`Launch URL not configured for ${app.displayName || app.name}`);
        return;
      }

      const slugParam = customerKey ? `?slug=${encodeURIComponent(customerKey)}` : "";
      const res = await fetch(apiUrl(`/api/v1/auth/return_code${slugParam}`), {
        method: "POST",
        credentials: "include",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 401) {
          if (typeof onLogout === "function") {
            await onLogout();
            return;
          }
          throw new Error("Not authenticated");
        }
        throw new Error(err?.error || "Failed to get launch code");
      }
      const { code } = await res.json();
      if (!code) throw new Error("No launch code returned");

      localStorage.setItem(
        LAST_LAUNCH_URL_KEY,
        sanitizeUrlStrippingAuthCallbackParams(app.launchUrl)
      );
      const sep = app.launchUrl.includes("?") ? "&" : "?";
      window.open(`${app.launchUrl}${sep}code=${encodeURIComponent(code)}`, "_blank");
    } catch (error) {
      const errorMessage = error.message || "Unknown error";
      setErrorModal({ message: errorMessage, details: "You are not authenticated." });
    }
  };

  const AppTile = ({ app, onLaunchApp }) => (
    <div
      className="bg-white rounded-lg border border-slate-200 p-4 pb-8 hover:shadow-md transition-shadow relative cursor-pointer"
      onClick={() => onLaunchApp(app.id || app.appId)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onLaunchApp(app.id || app.appId);
        }
      }}
      title={app.launchUrl || undefined}
      aria-label={`Launch ${app.displayName || app.name}${app.launchUrl ? ` (${app.launchUrl})` : ""}`}
    >
      <div className="flex flex-col items-center justify-center text-center pt-2">
        <div className="w-12 h-12 mb-3 flex items-center justify-center">
          {app.logoUrl ? (
            <img src={app.logoUrl} alt={app.displayName || app.name} className="w-full h-full object-contain" />
          ) : (
            <span className="text-3xl">{app.icon || "📱"}</span>
          )}
        </div>
        <h3 className="font-semibold text-slate-800 text-sm leading-tight line-clamp-2">{app.displayName || app.name}</h3>
      </div>
    </div>
  );

  const AppDetailsModal = ({ app, onClose, onLaunch }) => {
    if (!app) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-800">App Details</h2>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <div className="text-center mb-6">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                {app.logoUrl ? (
                  <img src={app.logoUrl} alt={app.displayName || app.name} className="w-full h-full object-contain" />
                ) : (
                  <span className="text-6xl">{app.icon || "📱"}</span>
                )}
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">{app.displayName || app.name}</h3>
              {app.slug && (
                <p className="text-sm text-slate-500 mb-2 font-mono">{app.slug}</p>
              )}
              {app.description && (
                <p className="text-slate-600">{app.description}</p>
              )}
            </div>

            <div className="space-y-4">
              <button
                onClick={() => {
                  onLaunch(app.id);
                  onClose();
                }}
                className="w-full px-4 py-3 text-white rounded-lg transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  backgroundColor: '#003D4C',
                  border: `1px solid #004D5C`
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#004D5C';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#003D4C';
                }}
              >
                Launch App
              </button>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Launch URL</label>
                <input
                  type="text"
                  value={app.launchUrl || 'No URL configured'}
                  readOnly
                  className="w-full px-3 py-2 border rounded-md bg-slate-50 text-slate-600 text-sm"
                  style={{ borderColor: '#E5E7EB' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ErrorModal = ({ error, onClose }) => {
    if (!error) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-red-600">Launch Error</h2>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <div className="text-center mb-6">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Failed to Launch App</h3>
            </div>

            <div className="mb-6">
              <p className="text-slate-600 text-sm leading-relaxed">{error.message}</p>
              {error.details && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md animate-notice-slide-in">
                  <p className="text-red-800 text-xs">{error.details}</p>
                </div>
              )}
            </div>

            <button
              onClick={onClose}
              className="w-full px-4 py-3 text-white rounded-lg transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                backgroundColor: '#003D4C',
                border: `1px solid #004D5C`
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#004D5C';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#003D4C';
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen w-screen bg-slate-50 font-sans text-slate-700 pb-12">
      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full">
            <Sidebar
              lastSignIn={usernameOrEmail?.lastSignIn}
              isCollapsed={false}
              onToggleCollapse={() => {}}
              pathname={pathname}
              onNavigate={navigate}
              hasUserManagementAccess={hasAccess}
              userManagementConfigured={userManagementConfigured}
              appSlugForNav={appSlugForNav}
            />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar
          lastSignIn={usernameOrEmail?.lastSignIn}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          pathname={pathname}
          onNavigate={navigate}
          hasUserManagementAccess={hasAccess}
          userManagementConfigured={userManagementConfigured}
          appSlugForNav={appSlugForNav}
        />
      </div>

      {/* Main Content */}
      <div className={`min-h-screen flex flex-col transition-all duration-300 ${sidebarCollapsed ? "md:ml-16" : "md:ml-72"}`}>
        {/* Header */}
        <header className="h-20 bg-white border-b border-slate-200 px-4 md:px-8 flex items-center">
          <div className="flex items-center justify-between w-full h-full">
            <div className="flex items-center gap-2 md:gap-4 min-h-0 h-full">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 shrink-0"
                aria-label="Toggle mobile menu"
              >
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
              <div className="flex items-center gap-3 h-full">
                <img
                  src={brand.logoSrc}
                  alt={brand.alt}
                  className="h-full w-auto max-w-[140px] object-contain object-left"
                  draggable={false}
                />
                <h1 className="text-lg md:text-2xl font-bold text-slate-800 whitespace-nowrap" id="dashboard-title">
                  OneDoor Platform{pageTitle ? ` > ${pageTitle}` : ""}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <EnvironmentPill className="shrink-0" />
              <UserDropdown
                usernameOrEmail={userProfile.name || userProfile.email}
                userProfile={userProfile}
                onLogout={onLogout}
                onOpenMyProfile={() => setMyProfileModalOpen(true)}
              />
              <MyProfileModal
                isOpen={myProfileModalOpen}
                onClose={() => setMyProfileModalOpen(false)}
                onOpenChangePassword={() => setChangePasswordModalOpen(true)}
              />
              <ChangePasswordModal
                isOpen={changePasswordModalOpen}
                onClose={() => setChangePasswordModalOpen(false)}
                onSuccess={() => setChangePasswordModalOpen(false)}
              />
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 md:p-8" role="main" aria-labelledby="dashboard-title">
          {adminView === "users" && (
            <AdminUsersPage customerSlug={customerKey} />
          )}
          {adminView === "roles" && (
            <AdminRolesPage
              customerSlug={customerKey}
              appSlug={pathAppSlug}
              selectedApp={selectedAppFromPath}
              apps={apps}
              pathname={pathname}
              onNavigate={navigate}
            />
          )}
          {adminView === "permission-groups" && (
            <AdminPermissionGroupsPage
              customerSlug={customerKey}
              appSlug={pathAppSlug}
              selectedApp={selectedAppFromPath}
              apps={apps}
              pathname={pathname}
              onNavigate={navigate}
            />
          )}
          {!adminView && (
          <>
          {loading ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">
                Loading Applications...
              </h3>
              <p className="text-slate-600">
                Please wait while we fetch your available apps.
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">
                Error Loading Apps
              </h3>
              <p className="text-slate-600 mb-4">
                {error}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                style={{
                  backgroundColor: '#003D4C',
                  border: '1px solid #004D5C'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#004D5C';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#003D4C';
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3" role="grid" aria-labelledby="applications-heading">
            {apps.map((app, index) => (
              <AppTile
                key={app.id || app.appId || `app-${index}`}
                app={app}
                onLaunchApp={handleAppClick}
              />
            ))}
          </div>

              {apps.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4" aria-hidden>⚠️</div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">
                    Your user/role profile does not have any ODP apps assigned.
                  </h3>
                  <p className="text-slate-600">
                    Please contact your customer success team.
                  </p>
                </div>
              )}
            </>
          )}
          </>
          )}
        </main>

        <Footer />
      </div>

      {/* App Details Modal */}
      {selectedApp && (
        <AppDetailsModal
          app={selectedApp}
          onClose={() => setSelectedApp(null)}
          onLaunch={handleAppClick}
        />
      )}

      {/* Error Modal */}
      {errorModal && (
        <ErrorModal
          error={errorModal}
          onClose={() => setErrorModal(null)}
        />
      )}

      <style>{`
        @media (max-width: 400px) {
          .grid-cols-3 {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 320px) {
          .grid-cols-3 {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
